import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiRow from '../shared/KpiRow';
import KpiTile from '../shared/KpiTile';
import ChartPanel from '../shared/ChartPanel';
import DateRangePicker from '../shared/DateRangePicker';
import DistributionBar from '../shared/DistributionBar';
import AnalysisTable from '../shared/AnalysisTable';
import { useAnalysisRange } from '../../../hooks/analysis/useAnalysisRange';
import { useMemberAnalysis } from '../../../hooks/analysis/useMemberAnalysis';
import { Granularity } from '../../../utils/analytics/types';
import { GuestRfm } from '../../../utils/analytics/memberMetrics';
import { formatCurrency, formatPercentage } from '../../../utils/formatters';
import { formatDate } from '../../../utils/dateUtils';
import { AXIS_PROPS, CHART_INK, SERIES_COLORS } from '../shared/chartTheme';

const MemberAnalysis: React.FC = () => {
  const { range, setRange } = useAnalysisRange();
  const [granularity, setGranularity] = useState<Granularity>('month');
  const data = useMemberAnalysis(range, granularity);

  const guestName = (guest: GuestRfm): string => {
    if (guest.memberId) return data.nameByMemberId.get(guest.memberId) ?? `Member #${guest.memberId}`;
    return `Guest (customer #${guest.customerId})`;
  };

  const ltvRows = data.guests.slice(0, 25).map(guest => ({
    name: guestName(guest),
    memberId: guest.memberId,
    segment: guest.segment,
    stays: guest.stays,
    revenue: guest.revenue,
    lastCheckOut: guest.lastCheckOut,
  }));

  return (
    <div>
      <div className="analysis-controls">
        <DateRangePicker value={range} onChange={setRange} />
        <div className="analysis-toggle-group" role="group" aria-label="Granularity">
          {(['week', 'month'] as Granularity[]).map(g => (
            <button key={g} className={granularity === g ? 'active' : ''} onClick={() => setGranularity(g)}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <KpiRow>
        <KpiTile label="Members" value={String(data.totalMembers)} />
        <KpiTile
          label="Repeat guest rate"
          value={data.repeatRate !== null ? formatPercentage(data.repeatRate) : '—'}
          note="arrivals from returning guests"
        />
        <KpiTile label="Churn-risk guests" value={String(data.churnRisk.length)} note="active last year, quiet since" />
        <KpiTile
          label="Avg party"
          value={`${data.party.avgAdults + data.party.avgChildren} people`}
          note={`${formatPercentage(data.party.withPetsPct)} bring pets`}
        />
      </KpiRow>

      <ChartPanel
        title="New vs returning guests"
        subtitle="Arrivals split by whether it's the guest's first stay on record"
        loading={data.loading}
        error={data.error}
        empty={data.repeatSeries.every(p => p.newGuests + p.repeatGuests === 0)}
        table={{
          columns: [
            { key: 'label', label: 'Period' },
            { key: 'newGuests', label: 'New', align: 'right' },
            { key: 'repeatGuests', label: 'Returning', align: 'right' },
          ],
          rows: data.repeatSeries,
        }}
      >
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.repeatSeries} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={24} />
            <YAxis {...AXIS_PROPS} allowDecimals={false} width={36} />
            <Tooltip />
            <Legend />
            <Bar dataKey="repeatGuests" name="Returning" stackId="guests" fill={SERIES_COLORS[0]} maxBarSize={48} />
            <Bar dataKey="newGuests" name="New" stackId="guests" fill={SERIES_COLORS[1]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ChartPanel
        title="Guest segments (RFM)"
        subtitle="Recency, frequency, and spend across all loaded stays"
        loading={data.loading}
        error={data.error}
        empty={data.segments.length === 0}
      >
        <DistributionBar
          segments={data.segments.map(segment => ({
            label: segment.segment,
            value: segment.guests,
            display: `${segment.guests} guests · ${formatCurrency(segment.revenue)}`,
          }))}
        />
      </ChartPanel>

      <ChartPanel
        title="Top guests by lifetime value"
        subtitle="Total spend across loaded history"
        loading={data.loading}
        error={data.error}
        empty={ltvRows.length === 0}
        csv={{
          filename: `guest-ltv-${range.from}-${range.to}`,
          columns: [
            { key: 'name', label: 'Guest' },
            { key: 'segment', label: 'Segment' },
            { key: 'stays', label: 'Stays' },
            { key: 'revenue', label: 'Revenue' },
            { key: 'lastCheckOut', label: 'Last check-out' },
          ],
          rows: ltvRows,
        }}
      >
        <AnalysisTable
          columns={[
            {
              key: 'name',
              label: 'Guest',
              format: v => String(v),
            },
            { key: 'segment', label: 'Segment' },
            { key: 'stays', label: 'Stays', align: 'right' },
            { key: 'revenue', label: 'Revenue', align: 'right', format: formatCurrency },
            { key: 'lastCheckOut', label: 'Last check-out', format: v => formatDate(String(v)) },
          ]}
          rows={ltvRows}
          defaultSortKey="revenue"
        />
        <p className="chart-panel-footnote">
          Member profiles: {ltvRows.filter(r => r.memberId).slice(0, 6).map((row, i) => (
            <span key={row.memberId}>
              {i > 0 && ' · '}
              <Link to={`/members/${row.memberId}`}>{row.name}</Link>
            </span>
          ))}
        </p>
      </ChartPanel>

      <div className="chart-panel-grid">
        <ChartPanel
          title="Member cohorts by signup year"
          loading={data.loading}
          error={data.error}
          empty={data.cohorts.length === 0}
        >
          <AnalysisTable
            columns={[
              { key: 'cohort', label: 'Signed up' },
              { key: 'members', label: 'Members', align: 'right' },
              { key: 'stays', label: 'Stays', align: 'right' },
              { key: 'revenue', label: 'Revenue', align: 'right', format: formatCurrency },
            ]}
            rows={data.cohorts}
            defaultSortKey="cohort"
          />
        </ChartPanel>

        <ChartPanel
          title="Churn-risk guests"
          subtitle="Repeat guests who haven't returned in over a year"
          loading={data.loading}
          error={data.error}
          empty={data.churnRisk.length === 0}
          emptyMessage="No repeat guests are drifting away. 🎉"
        >
          <AnalysisTable
            columns={[
              { key: 'name', label: 'Guest' },
              { key: 'stays', label: 'Stays', align: 'right' },
              { key: 'revenue', label: 'Revenue', align: 'right', format: formatCurrency },
              { key: 'lastCheckOut', label: 'Last visit', format: v => formatDate(String(v)) },
            ]}
            rows={data.churnRisk.map(guest => ({ ...guest, name: guestName(guest) }))}
            defaultSortKey="revenue"
            maxRows={15}
          />
        </ChartPanel>
      </div>

      {data.reviews && data.reviews.total > 0 && (
        <ChartPanel
          title="Guest review ratings"
          subtitle="Store-wide average rating per month (hcp-saas aggregate)"
          loading={data.aggregatesLoading}
          error={null}
          empty={data.reviews.monthly.length === 0}
        >
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.reviews.monthly} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
              <XAxis dataKey="month" {...AXIS_PROPS} minTickGap={24} />
              <YAxis {...AXIS_PROPS} domain={[1, 5]} width={32} />
              <Tooltip formatter={(value: any) => [Number(value).toFixed(2), 'Avg rating']} />
              <Line type="monotone" dataKey="avg_rating" stroke={SERIES_COLORS[0]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
          {data.reviews.would_welcome_back_pct !== null && (
            <p className="chart-panel-footnote">
              {formatPercentage(data.reviews.would_welcome_back_pct)} of reviewed guests would be welcomed back ·
              {' '}{data.reviews.total} reviews
            </p>
          )}
        </ChartPanel>
      )}

      {data.rvDemographics && data.rvDemographics.total > 0 && (
        <ChartPanel
          title="RV demographics"
          subtitle="Active member RVs by type (hcp-saas aggregate)"
          loading={data.aggregatesLoading}
          error={null}
          empty={data.rvDemographics.types.length === 0}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.rvDemographics.types} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
              <XAxis dataKey="type" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} allowDecimals={false} width={36} />
              <Tooltip formatter={(value: any) => [value, 'RVs']} />
              <Bar dataKey="count" fill={SERIES_COLORS[0]} radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}
    </div>
  );
};

export default MemberAnalysis;
